import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useContext, useEffect, useReducer, useState } from 'react'
import { useForm } from 'react-hook-form'

import { cartService } from '@/domain/Cart'
import { UpdateCartDto } from '@/domain/Cart/dtos/update-cart.dto'
import { customerService } from '@/domain/Customer'
import { UpdateShippingAddressDto } from '@/domain/Customer/dtos/update-shipping-address.dto'
import { ShippingAddressDto } from '@/domain/shared/dtos/shipping-address.dto'
import { ShippingAddressSchema } from '@/domain/shared/schemas/shipping-address.schema'
import { CartContext } from '@/modules/Cart/Cart.vm'
import { Address } from '@medusajs/medusa'
import { toast } from 'react-toastify'
import { Customer } from 'stoux-shared-types/models/customer'

type CustomerShippingOptionsFormValues = {
    mode: 'CREATE' | 'UPDATE'
    isModalOpen: boolean
    address_id: string | null
}

const SHIPPING_ADDRESS_LIMIT_ANONYMOUS_MODE = 1

export const useViewModel = () => {
    const {
        userLogged,
        cartActor: [state, send],
        onUpdateCart,
        onUpdateUserLogged,
    } = useContext(CartContext)

    const [isLoading, setLoading] = useState(false)
    const [shippingAddresses, setShippingAddresses] = useState<Address[]>([])

    const isAnonymous = state.context.options.anonymous_mode
    const hasReachedShippingAddressLimit =
        isAnonymous &&
        shippingAddresses.length >= SHIPPING_ADDRESS_LIMIT_ANONYMOUS_MODE

    const createShippingAddressMutation = useMutation({
        mutationFn: (dto: ShippingAddressDto) => customerService.createShippingAddress(dto)
    })

    const updateShippingAddressMutation = useMutation({
        mutationFn: (dto: UpdateShippingAddressDto) =>
            customerService.updateShippingAddress(dto)
    })

    const updateCartMutation = useMutation({
        mutationFn: (dto: UpdateCartDto) =>
            cartService.updateCart(dto)
    })

    const form = useForm<ShippingAddressDto>({
        mode: 'all',
        defaultValues: {
            country_code: 'fr',
        },
        resolver: zodResolver(ShippingAddressSchema),
    })

    const optionsForm = useForm<CustomerShippingOptionsFormValues>({
        defaultValues: {
            mode: 'CREATE',
            isModalOpen: false,
        },
    })

    const onSelectAddress = (address_id: string) => {
        if (!state.context.cart) {
            throw new Error('Cart not found')
        }

        const foundAddress = shippingAddresses?.find(
            (address) => address.id === address_id,
        )

        if (!foundAddress) {
            return
        }

        send({
            type: 'UPDATE_OPTIONS',
            updatedOptions: {
                ...state.context.options,
                shipping_address: foundAddress,
            },
        })
    }

    /**
     * ? Used to submit the shippingAddressForm when CREATING a new shipping address
     */
    const onCreateShippingAddressSubmit = form.handleSubmit(async (data) => {
        try {
            if (!state.context.cart) {
                throw new Error('Cart not found')
            }

            if (!isAnonymous) {
                const updatedCustomer =
                    await createShippingAddressMutation.mutateAsync(data)
                onUpdateUserLogged(updatedCustomer)
            }

            await updateCartMutation.mutateAsync(
                {
                    cart_id: state.context.cart.id,
                    shipping_address: data,
                },
                {
                    onSuccess: (updatedCart) => {
                        onUpdateCart(updatedCart)

                        if (isAnonymous) {
                            setShippingAddresses([
                                updatedCart.shipping_address!,
                            ])
                        }
                    },
                },
            )

            optionsForm.setValue('isModalOpen', false)
            form.reset()
        } catch (err) {
            console.error(err)
        } finally {
        }
    })

    /**
     * ? Used to submit the shippingAddressForm when UPDATING a shipping address
     */
    const onEditShippingAddressSubmit = form.handleSubmit(
        async (data: ShippingAddressDto) => {
            try {
                if (!state.context.cart) {
                    throw new Error('Cart not found')
                }

                const address_id = optionsForm.getValues('address_id')

                if (!address_id) {
                    throw new Error('Address id not found')
                }

                if (!isAnonymous) {
                    const updatedCustomer =
                        await updateShippingAddressMutation.mutateAsync({
                            ...data,
                            address_id,
                        })
                    onUpdateUserLogged(updatedCustomer)
                }

                await updateCartMutation.mutateAsync(
                    {
                        cart_id: state.context.cart.id,
                        shipping_address: data,
                    },
                    {
                        onSuccess: (updatedCart) => {
                            onUpdateCart(updatedCart)

                            if (isAnonymous) {
                                setShippingAddresses([
                                    updatedCart.shipping_address!,
                                ])
                            }
                        },
                    },
                )

                optionsForm.setValue('isModalOpen', false)
                form.reset()
            } catch (err) {
                console.error(`Edit shipping address error: ${err}`)
            }
        },
    )

    /**
     * ? Used to submit the shippingAddressForm
     */
    const onFormSubmit = async () => {
        const mode = optionsForm.getValues('mode')
        form.setValue('country_code', 'fr')

        if (mode === 'CREATE') {
            return await onCreateShippingAddressSubmit()
        }

        return await onEditShippingAddressSubmit()
    }

    const onCreate = () => {
        if (
            isAnonymous &&
            shippingAddresses.length === SHIPPING_ADDRESS_LIMIT_ANONYMOUS_MODE
        ) {
            toast.error(
                `Vous ne pouvez avoir qu'une seule adresse de livraison en tant que client anonyme.`,
            )
            return
        }
        form.reset({})
        optionsForm.reset({
            address_id: null,
            mode: 'CREATE',
            isModalOpen: true,
        })
    }

    const onEdit = (address_id: string) => {
        const foundShippingAddress = shippingAddresses?.find(
            (addr) => addr.id === address_id,
        )

        if (!foundShippingAddress) {
            return
        }

        const validatedShippingAddress =
            ShippingAddressSchema.safeParse(foundShippingAddress)

        if (!validatedShippingAddress.success) {
            return
        }

        optionsForm.reset({
            mode: 'UPDATE',
            isModalOpen: true,
            address_id,
        })

        form.reset({
            ...validatedShippingAddress.data,
        })
    }

    const onNext = async () => {
        if (!state.context.options.shipping_address || !state.context.cart) {
            return
        }

        await updateCartMutation.mutateAsync({
            cart_id: state.context.cart.id,
            shipping_address: state.context.options
                .shipping_address as UpdateCartDto['shipping_address'],
        })

        send({
            type: 'NEXT',
        })
    }

    useEffect(() => {
        if (isAnonymous || !userLogged || !userLogged.shipping_addresses) {
            setLoading(false)
            return
        }

        setShippingAddresses(userLogged.shipping_addresses as any)
        setLoading(false)
    }, [isAnonymous, userLogged, userLogged?.shipping_addresses])

    return {
        form,
        optionsForm,
        selectedShippingAddress: state.context.options.shipping_address,
        shippingAddresses,
        isLoading,
        hasReachedShippingAddressLimit,
        onSelectAddress,
        onNext,
        onFormSubmit,
        onCreate,
        onEdit,
    }
}
